import type { ComputedRef, Ref } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { useSidebarPrivileges } from '@entities/viewer'
import { useDayjs } from '@shared/lib/format'

import { usePromocodesStore } from './store'
import { usePromocodesApi } from '../api/list.hook'
import { toDiscountOptions, usePromocodeOptions } from './promocode-options'

interface FiltersPromocodesReturn {
  refSearch: Ref<string>
  refStatus: Ref<string | undefined>
  refDiscount: Ref<number | undefined>
  refDateRange: Ref<Date[] | null>
  statusOptions: ComputedRef<AppSelectOption[]>
  discountOptions: ComputedRef<AppSelectOption<number>[]>
  canManage: ComputedRef<boolean>
  isLoading: ComputedRef<boolean>
  submitSearch: () => Promise<void>
  clearFilters: () => Promise<void>
  openCreateModal: () => void
}

/** The controls hold a draft; nothing is requested until "Search" or Enter in the search input. */
export const useFiltersPromocodes = (): FiltersPromocodesReturn => {
  const router = useRouter()
  const route = useRoute()
  const store = usePromocodesStore()
  const dayjs = useDayjs()
  const { statusOptions } = usePromocodeOptions()
  const { canManagePromocodes } = useSidebarPrivileges()

  const refSearch = ref<string>('')
  const refStatus = ref<string | undefined>(undefined)
  const refDiscount = ref<number | undefined>(undefined)
  const refDateRange = ref<Date[] | null>(null)

  const discountOptions = computed<AppSelectOption<number>[]>(() => toDiscountOptions(store.getDiscounts))
  const canManage = computed<boolean>(() => canManagePromocodes.value)
  const isLoading = computed<boolean>(() => store.isLoading)

  const toDateRange = (from: string | null, to: string | null): Date[] | null => {
    if (!from || !to) return null

    const fromDate = dayjs(from)
    const toDate = dayjs(to)

    return fromDate.isValid() && toDate.isValid() ? [fromDate.toDate(), toDate.toDate()] : null
  }

  const syncDraft = (): void => {
    refSearch.value = store.getFilterSearch ?? ''
    refStatus.value = store.getFilterStatus ?? undefined
    refDiscount.value = store.getFilterDiscount ?? undefined
    refDateRange.value = toDateRange(store.getFilterFrom, store.getFilterTo)
  }

  /** The API takes the range only as a pair, so a half-picked range is not sent. */
  const applyDraftRange = (): void => {
    const [from, to] = refDateRange.value ?? []

    store.setFilterFrom(from && to ? dayjs(from).format(DateFormat.Default) : null)
    store.setFilterTo(from && to ? dayjs(to).format(DateFormat.Default) : null)
  }

  /** `page` is dropped so a new filter never lands on a page that no longer exists. */
  const syncQueryAndReload = async (): Promise<void> => {
    await router.push({
      query: {
        ...route.query,
        q: store.getFilterSearch || undefined,
        status: store.getFilterStatus || undefined,
        discount: store.getFilterDiscount !== null ? String(store.getFilterDiscount) : undefined,
        from: store.getFilterFrom || undefined,
        to: store.getFilterTo || undefined,
        page: undefined,
      },
    })
    await usePromocodesApi()
  }

  const submitSearch = async (): Promise<void> => {
    store.setFilterSearch(refSearch.value.trim() || null)
    store.setFilterStatus(refStatus.value ?? null)
    store.setFilterDiscount(refDiscount.value ?? null)
    applyDraftRange()

    await syncQueryAndReload()
  }

  const clearFilters = async (): Promise<void> => {
    store.resetFilters()
    syncDraft()

    await syncQueryAndReload()
  }

  const openCreateModal = (): void => {
    store.setModalCreate(true)
  }

  // The store, not the URL: the list hydrates it from the query, and the draft follows once it has.
  watch(
    () => [
      store.getFilterSearch,
      store.getFilterStatus,
      store.getFilterDiscount,
      store.getFilterFrom,
      store.getFilterTo,
    ],
    syncDraft,
    { immediate: true, deep: true },
  )

  return {
    refSearch,
    refStatus,
    refDiscount,
    refDateRange,
    statusOptions,
    discountOptions,
    canManage,
    isLoading,
    submitSearch,
    clearFilters,
    openCreateModal,
  }
}
